import { unwrapResult } from '@reduxjs/toolkit';
import { useNavigate } from 'react-router';
import { slices, useAppDispatch, useAppSelector } from 'src/redux';
import {
  ChequeListPayload,
  CreateChequeApiPayload,
  UpdateChequeApiPayload
} from 'src/redux/slices/cheque-management';
import { useSnackBar } from '../use-snackbar';
import { formatChequeListParams } from 'src/utils';

const {
  selectors: chequeSelectors,
  actions: chequeActions
} = slices.chequeManagement;

export const useCheque = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const snackBar = useSnackBar();

  const isLoading = useAppSelector(chequeSelectors.selectIsLoading);

  const chequeList = useAppSelector(chequeSelectors.selectChequeList);
  const chequeDetails = useAppSelector(chequeSelectors.selectChequeDetails);
  const chequeListMeta = useAppSelector(chequeSelectors.selectChequeListMeta);
  const chequeListParams = useAppSelector(
    chequeSelectors.selectChequeListParams
  );
  const chequeListColumn = useAppSelector(
    chequeSelectors.selectChequeListColumn
  );

  const createCheque = async (
    params: CreateChequeApiPayload
  ): Promise<{ success: boolean; id: number }> => {
    try {
      const response = unwrapResult(
        await dispatch(chequeActions.createChequeThunk(params))
      );

      if (response?.success) {
        return {
          success: true,
          id: response?.originalData?.data?.cheque_id ?? 0
        };
      }

      if (!response?.success) {
        if (!response?.success) {
          const errors = response?.errors || {};
          const errorMessages = Object.values(errors)
            .flat()
            .join('\n');

          snackBar.show({
            severity: 'error',
            message: errorMessages
          });
        }

        return { success: false, id: 0 };
      }

      return { success: false, id: 0 };
    } catch (error) {
      console.error('Error fetching cheque details:', error);
      return { success: false, id: 0 };
    }
  };

  const updateCheque = async (
    params: UpdateChequeApiPayload,
    reloadPage: boolean,
    makeCallback?: () => void
  ) => {
    try {
      const response = unwrapResult(
        await dispatch(chequeActions.updateChequeThunk(params))
      );

      if (response?.success) {
        if (reloadPage) {
          window.location.reload();
          return;
        }
        //call a callback function after
        if (makeCallback) {
          makeCallback();
        }
      }

      if (!response?.success) {
        const errors = response?.errors || {};
        const errorMessages = Object.values(errors)
          .flat()
          .join('\n');

        snackBar.show({
          severity: 'error',
          message: errorMessages
        });
      }
    } catch (error) {
      console.error('Error fetching cheque details:', error);
    }
  };

  const fetchChequeList = async (params: ChequeListPayload) => {
    try {
      const response = unwrapResult(
        await dispatch(chequeActions.chequeListThunk(params))
      );

      const urlParams = formatChequeListParams(params);

      if (
        response?.success &&
        response?.originalData?.data &&
        response?.originalData?.data.length <= 0
      ) {
        snackBar.show({
          severity: 'info',
          message: 'No cheque found'
        });

        // Reset to page 1 if not already page 1
        if (params.page !== 1) {
          const newParams = { ...params, page: 1 };
          const newUrlParams = formatChequeListParams(newParams);
          navigate(`/app/cheque?${newUrlParams.toString()}`);
          return; // Prevent further navigation with old params
        }
      }

      if (response?.success) {
        navigate(`/app/cheque?${urlParams.toString()}`);
      }
    } catch (error) {
      console.error('Error fetching cheque list:', error);
    }
  };

  const fetchChequeDetails = async (cheque_id: number): Promise<boolean> => {
    try {
      await dispatch(chequeActions.chequeDetailsThunk(cheque_id));
      return true;
    } catch (error) {
      console.error('Error fetching cheque details:', error);
      return false;
    }
  };

  const deleteCheque = async (cheque_id: number) => {
    try {
      const response = unwrapResult(
        await dispatch(chequeActions.deleteChequeThunk(cheque_id))
      );

      if (response?.success) {
        navigate('/app/cheque');
      }
    } catch (error) {
      console.error('Error deleting cheque details:', error);
    }
  };

  const setChequeListParams = async (params: ChequeListPayload) => {
    try {
      dispatch(chequeActions.chequeFilterParams(params));
    } catch (error) {
      console.error('Error fetching cheque details:', error);
    }
  };

  const toggleChequeColumnFilter = async (key: string) => {
    dispatch(chequeActions.toggleChequeColumn(key));
  };

  const onToggleAll = async () => {
    dispatch(chequeActions.toggleAll());
  };

  return {
    isLoading,
    chequeList,
    chequeDetails,
    chequeListMeta,
    chequeListParams,
    chequeListColumn,
    createCheque,
    updateCheque,
    fetchChequeList,
    fetchChequeDetails,
    setChequeListParams,
    deleteCheque,
    toggleChequeColumnFilter,
    onToggleAll
  };
};
